// Data sets section (super admins): whose data, in which data model (EFD /
// PPI) and variant, and how to reach it — database, schemas, optionally an
// SSH jump host. The assistants dock onto data sets of their own model
// (src/data_agent/assistants.py). Secrets are write-only —
// once saved the server only says that one is stored. Tests run ON the
// managed instance (only it can reach its databases): src/data_agent/source_checks.py.

import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faPlus, faTrash, faFloppyDisk, faPlug, faServer, faKey, faLock, faTriangleExclamation,
  faCircleInfo, faArrowsRotate, faRotateLeft, faCopy, faArrowRight, faXmark, faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import { Field, Switch, PasswordInput, ConfirmDialog, StatusIcon, CheckRows, Spinner, relativeTime, formatDate } from './ui';
import { useConsole } from './context';

const EMPTY_SOURCE = {
  key: '', name: '', description: '', db_host: '', db_port: 5432, db_name: '', db_user: '',
  ssh_enabled: false, ssh_host: '', ssh_port: 22, ssh_user: '', ssh_auth: 'key', schema_map: {},
  model: 'efd', variant: 'mh',
};

const mapRows = (m) => Object.entries(m || {}).map(([from, to]) => ({ from, to }));
const rowsToMap = (rows) => Object.fromEntries(rows
  .map((r) => [r.from.trim().toLowerCase(), r.to.trim().toLowerCase()])
  .filter(([from, to]) => from || to));
const sameMap = (a, b) => JSON.stringify(Object.entries(a || {}).sort()) === JSON.stringify(Object.entries(b || {}).sort());

/** {logical: physical} schema names — the same data model under other schemas. */
function SchemaMapEditor({ rows, onChange, error }) {
  const update = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="um-schemamap">
      {rows.length === 0 && <div className="um-empty-inline">No mapping — queries read the schemas as the assistant names them.</div>}
      {rows.map((r, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="um-schemamap-row">
          <input className="um-mono" value={r.from} placeholder="mh" onChange={(e) => update(i, 'from', e.target.value)} aria-label="Schema the assistant uses" />
          <FontAwesomeIcon icon={faArrowRight} className="um-muted" />
          <input className="um-mono" value={r.to} placeholder="sales" onChange={(e) => update(i, 'to', e.target.value)} aria-label="Schema read instead" />
          <button type="button" className="um-icon-btn danger" title="Remove" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      ))}
      <button type="button" className="um-add-btn" onClick={() => onChange([...rows, { from: '', to: '' }])}>
        <FontAwesomeIcon icon={faPlus} /> Add mapping
      </button>
      {error && <div className="um-hint error">{error}</div>}
    </div>
  );
}

function DuplicateDialog({ source, onDone, onCancel }) {
  const { client } = useConsole();
  const [name, setName] = useState(`${source.name} (copy)`);
  const [key, setKey] = useState(slug(`${source.key}-copy`));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try { onDone(await client.post(`/api/admin/data-sources/${source.id}/duplicate`, { key, name })); } catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <div className="um-overlay" onClick={onCancel}>
      <form className="um-dialog wide" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3><FontAwesomeIcon icon={faCopy} /> Duplicate {source.name}</h3>
        <p>Same database, login and SSH jump host — the stored credentials are copied on the server and never shown. Change what differs afterwards, e.g. a schema mapping for an anonymised copy.</p>
        <div className="um-dialog-fields">
          <Field label="Name"><input value={name} autoFocus onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Key"><input value={key} className="um-mono" onChange={(e) => setKey(slug(e.target.value))} /></Field>
        </div>
        {error && <div className="um-hint error">{error}</div>}
        <div className="um-dialog-btns">
          <button type="button" className="um-btn um-btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="um-btn um-btn-primary" disabled={busy || !name || !key}>{busy ? 'Copying…' : 'Duplicate'}</button>
        </div>
      </form>
    </div>
  );
}

const VARIANT_HINT = 'How this client’s data was loaded — decides how the assistant reads it.';

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

/** A stored secret: shown as stored, replaced only on purpose. */
function SecretField({ label, stored, value, onChange, placeholder, multiline, hint, error }) {
  const [replacing, setReplacing] = useState(!stored);
  useEffect(() => { setReplacing(!stored); }, [stored]);
  return (
    <Field label={label} hint={hint} error={error} wide={multiline}>
      {!replacing ? (
        <div className="um-secret-stored">
          <FontAwesomeIcon icon={faLock} /> Stored (encrypted) — never shown again
          <button type="button" className="um-link-btn" onClick={() => setReplacing(true)}>Replace</button>
        </div>
      ) : multiline ? (
        <textarea rows={6} className="um-mono" value={value || ''} placeholder={placeholder} spellCheck={false}
          onChange={(e) => onChange(e.target.value)} />
      ) : (
        <PasswordInput value={value || ''} onChange={onChange} placeholder={placeholder} autoComplete="new-password" />
      )}
      {replacing && stored && (
        <button type="button" className="um-link-btn" onClick={() => { onChange(''); setReplacing(false); }}>Keep the stored one</button>
      )}
    </Field>
  );
}

function TestResult({ result }) {
  if (!result) return null;
  if (result.error) return <div className="um-hint error">{result.error}</div>;
  return (
    <div className={`um-check-card ${result.status}`}>
      <div className="um-check-card-head">
        <StatusIcon status={result.status} />
        <strong>{result.status === 'ok' ? 'Connection works' : result.status === 'warn' ? 'Connection works — with warnings' : 'Connection failed'}</strong>
      </div>
      <CheckRows rows={result.steps || []} />
    </div>
  );
}

function SourceEditor({ original, isNew, onClose, onSaved, onDeleted, flash, initialResult }) {
  const { client, meta } = useConsole();
  const [form, setForm] = useState(() => ({ ...EMPTY_SOURCE, ...original }));
  const [secrets, setSecrets] = useState({ db_password: '', ssh_private_key: '', ssh_password: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(initialResult || null);
  const [confirm, setConfirm] = useState(null);
  const [keyEdited, setKeyEdited] = useState(!isNew);
  const [schemaRows, setSchemaRows] = useState(() => mapRows(original.schema_map));

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v, ...(k === 'name' && !keyEdited ? { key: slug(v) } : {}) }));
    setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
  };
  const setSecret = (k, v) => setSecrets((s) => ({ ...s, [k]: v }));

  const payload = () => {
    const p = {
      key: form.key, name: form.name, description: form.description, model: form.model, variant: form.variant,
      db_host: form.db_host,
      db_port: Number(form.db_port), db_name: form.db_name, db_user: form.db_user, ssh_enabled: !!form.ssh_enabled,
    };
    if (form.ssh_enabled) Object.assign(p, { ssh_host: form.ssh_host, ssh_port: Number(form.ssh_port), ssh_user: form.ssh_user, ssh_auth: form.ssh_auth });
    Object.entries(secrets).forEach(([k, v]) => { if (v) p[k] = v; });
    p.schema_map = rowsToMap(schemaRows);
    return p;
  };

  const fail = (e) => {
    setErrors(Object.fromEntries((e.fields || []).map((f) => [f, e.message])));
    flash('error', e.message);
  };

  const test = async () => {
    setBusy('test');
    setResult(null);
    try {
      const r = await client.post('/api/admin/data-sources/test', { ...payload(), ...(isNew ? {} : { id: original.id }) });
      setResult(r.result);
    } catch (e) { fail(e); }
    setBusy('');
  };

  const save = async () => {
    setBusy('save');
    try {
      const saved = isNew
        ? await client.post('/api/admin/data-sources', payload())
        : await client.put(`/api/admin/data-sources/${original.id}`, payload());
      flash('success', isNew ? `Data source ${saved.name} created — run the test once to pin the SSH host key.` : 'Data source saved.');
      setSecrets({ db_password: '', ssh_private_key: '', ssh_password: '' });
      onSaved(saved);
    } catch (e) { fail(e); }
    setBusy('');
  };

  const testSaved = async () => {
    setBusy('test');
    setResult(null);
    try {
      const r = await client.post(`/api/admin/data-sources/${original.id}/test`);
      setResult(r.result);
      onSaved(r.source, r.result);
    } catch (e) { fail(e); }
    setBusy('');
  };

  const resetHostKey = async () => {
    setConfirm(null);
    try {
      const s = await client.post(`/api/admin/data-sources/${original.id}/reset-host-key`);
      flash('success', 'Pinned host key removed — the next test pins the current one.');
      onSaved(s);
    } catch (e) { fail(e); }
  };

  const remove = async () => {
    setConfirm(null);
    try {
      await client.del(`/api/admin/data-sources/${original.id}`);
      flash('success', `Data source ${original.name} deleted.`);
      onDeleted(original.id);
    } catch (e) { flash('error', e.message); }
  };

  const changed = isNew || Object.values(secrets).some(Boolean) || !sameMap(rowsToMap(schemaRows), original.schema_map)
    || ['name', 'description', 'model', 'variant', 'db_host', 'db_port', 'db_name', 'db_user', 'ssh_enabled', 'ssh_host', 'ssh_port', 'ssh_user', 'ssh_auth']
      .some((k) => String(form[k] ?? '') !== String(original[k] ?? ''));

  return (
    <>
      <div className="um-section-bar">
        <div className="um-topbar-left">
          <button type="button" className="um-back-btn" onClick={onClose} title="Back"><FontAwesomeIcon icon={faArrowLeft} /></button>
          <div>
            <h2 className="um-h2">{isNew ? 'New data set' : original.name}</h2>
            {!isNew && <div className="um-subtitle"><code>{original.key}</code> · {original.target}</div>}
          </div>
        </div>
        <div className="um-topbar-right">
          <button type="button" className="um-btn um-btn-ghost" onClick={changed ? test : testSaved} disabled={!!busy}>
            {busy === 'test' ? <Spinner small /> : <FontAwesomeIcon icon={faPlug} />} {changed && !isNew ? 'Test changes' : 'Test connection'}
          </button>
          {!isNew && (
            <button type="button" className="um-btn um-btn-ghost" onClick={() => setConfirm('duplicate')} disabled={!!busy}
              title="A copy with the same database and credentials">
              <FontAwesomeIcon icon={faCopy} /> Duplicate
            </button>
          )}
          <button type="button" className="um-btn um-btn-primary" onClick={save} disabled={!!busy || !changed}>
            <FontAwesomeIcon icon={faFloppyDisk} /> {busy === 'save' ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>

      <div className="um-panel">
        <TestResult result={result} />

        <section className="um-block">
          <h4 className="um-block-title">General</h4>
          <div className="um-grid-2">
            <Field label="Whose data" error={errors.name} hint="The client's name — this is what everybody sees (e.g. Marcuard Heritage, survista).">
              <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Marcuard Heritage" />
            </Field>
            <Field label="Key" error={errors.key} hint={isNew ? 'Users and assistants reference it — cannot be changed later.' : 'Cannot be changed.'}>
              <input value={form.key} disabled={!isNew} className="um-mono"
                onChange={(e) => { setKeyEdited(true); set('key', slug(e.target.value)); }} placeholder="mh-prod" />
            </Field>
            <Field label="Data model" error={errors.model}
              hint={meta.data_models?.find((m) => m.key === form.model)?.description}>
              <div className="um-segmented">
                {(meta.data_models || []).map((m) => (
                  <button key={m.key} type="button" className={form.model === m.key ? 'on' : ''}
                    onClick={() => { set('model', m.key); set('variant', (meta.variants || []).find((v) => v.model === m.key)?.key || ''); }}>
                    {m.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Variant" error={errors.variant}
              hint={meta.variants?.find((v) => v.key === form.variant)?.description || VARIANT_HINT}>
              <select value={form.variant || ''} onChange={(e) => set('variant', e.target.value)}>
                {!form.variant && <option value="">Choose…</option>}
                {(meta.variants || []).filter((v) => v.model === form.model).map((v) => (
                  <option key={v.key} value={v.key}>{v.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Description" wide error={errors.description}>
              <input value={form.description || ''} onChange={(e) => set('description', e.target.value)} placeholder="What this database holds, who runs it" />
            </Field>
          </div>
        </section>

        <section className="um-block">
          <h4 className="um-block-title"><FontAwesomeIcon icon={faServer} />&nbsp; Database</h4>
          <div className="um-grid-2">
            <Field label="Host" error={errors.db_host} hint={form.ssh_enabled ? 'As seen from the jump host (e.g. its internal IP).' : 'As seen from this instance.'}>
              <input value={form.db_host} onChange={(e) => set('db_host', e.target.value)} placeholder="10.19.79.3" />
            </Field>
            <Field label="Port" error={errors.db_port}>
              <input type="number" value={form.db_port} onChange={(e) => set('db_port', e.target.value)} />
            </Field>
            <Field label="Database" error={errors.db_name}>
              <input value={form.db_name} onChange={(e) => set('db_name', e.target.value)} placeholder="ip_banking" />
            </Field>
            <Field label="User" error={errors.db_user} hint="Ideally a read-only user — HeyBro sessions are read-only anyway.">
              <input value={form.db_user} onChange={(e) => set('db_user', e.target.value)} placeholder="ip_banking" autoComplete="off" />
            </Field>
            <SecretField label="Password" stored={!isNew && original.has_db_password} value={secrets.db_password}
              onChange={(v) => setSecret('db_password', v)} error={errors.db_password} />
          </div>
        </section>

        <section className="um-block">
          <h4 className="um-block-title"><FontAwesomeIcon icon={faLayerGroup} />&nbsp; Schemas</h4>
          <div className="um-hint">
            Optional. Reads the same data model under other schema names — e.g. the anonymised copy of Marcuard
            Heritage: <code>mh</code> → <code>sales</code>, <code>mh_chf</code> → <code>sales_chf</code>. A mapped source
            reads <strong>only</strong> its target schemas; everything else is refused.
          </div>
          <SchemaMapEditor rows={schemaRows} error={errors.schema_map}
            onChange={(r) => { setSchemaRows(r); setErrors((e) => { const n = { ...e }; delete n.schema_map; return n; }); }} />
        </section>

        <section className="um-block">
          <h4 className="um-block-title"><FontAwesomeIcon icon={faKey} />&nbsp; SSH jump host</h4>
          <Switch checked={form.ssh_enabled} onChange={(v) => set('ssh_enabled', v)}
            label={form.ssh_enabled ? 'Connect through an SSH jump host' : 'Direct connection (database reachable from this instance)'} />
          {form.ssh_enabled && (
            <div className="um-grid-2" style={{ marginTop: 14 }}>
              <Field label="SSH host" error={errors.ssh_host}>
                <input value={form.ssh_host} onChange={(e) => set('ssh_host', e.target.value)} placeholder="5.182.249.217" />
              </Field>
              <Field label="SSH port" error={errors.ssh_port}>
                <input type="number" value={form.ssh_port} onChange={(e) => set('ssh_port', e.target.value)} />
              </Field>
              <Field label="SSH user" error={errors.ssh_user} hint="Best a dedicated user that may only forward to this database.">
                <input value={form.ssh_user} onChange={(e) => set('ssh_user', e.target.value)} placeholder="heybro-tunnel" autoComplete="off" />
              </Field>
              <Field label="Authentication">
                <div className="um-segmented">
                  <button type="button" className={form.ssh_auth === 'key' ? 'on' : ''} onClick={() => set('ssh_auth', 'key')}>Private key</button>
                  <button type="button" className={form.ssh_auth === 'password' ? 'on' : ''} onClick={() => set('ssh_auth', 'password')}>Password</button>
                </div>
              </Field>
              {form.ssh_auth === 'key' ? (
                <SecretField label="Private key" multiline stored={!isNew && original.has_ssh_key} value={secrets.ssh_private_key}
                  onChange={(v) => setSecret('ssh_private_key', v)} error={errors.ssh_private_key}
                  placeholder={'-----BEGIN OPENSSH PRIVATE KEY-----\n…\n-----END OPENSSH PRIVATE KEY-----'}
                  hint="OpenSSH Ed25519, ECDSA or RSA key without passphrase. Windows line endings are fine." />
              ) : (
                <SecretField label="SSH password" stored={!isNew && original.has_ssh_password} value={secrets.ssh_password}
                  onChange={(v) => setSecret('ssh_password', v)} error={errors.ssh_password} />
              )}
              {!isNew && (
                <Field label="Pinned host key" wide
                  hint={original.ssh_host_key ? 'Connections are refused if the jump host presents a different key.' : 'Not pinned yet — the first successful test pins it. Agents only connect through a pinned host.'}>
                  <div className="um-secret-stored">
                    {original.ssh_host_key ? <code>{original.ssh_host_key}</code> : <span className="um-muted">— not pinned —</span>}
                    {original.ssh_host_key && (
                      <button type="button" className="um-link-btn" onClick={() => setConfirm('reset')}><FontAwesomeIcon icon={faRotateLeft} /> Reset</button>
                    )}
                  </div>
                </Field>
              )}
            </div>
          )}
        </section>

        {!isNew && (
          <section className="um-block um-meta-row">
            <div><span>Last test</span><strong>{original.last_test_at ? `${formatDate(original.last_test_at)} · ${original.last_test_ok ? 'OK' : 'failed'}` : 'never'}</strong></div>
            <div><span>Used by assistants</span><strong>{original.usage?.assistants?.join(', ') || '—'}</strong></div>
            <div><span>Used by users</span><strong>{original.usage?.users?.join(', ') || '—'}</strong></div>
            <div><span>Changed</span><strong>{original.updated_at ? `${formatDate(original.updated_at)} by ${original.updated_by || '?'}` : '—'}</strong></div>
          </section>
        )}

        {!isNew && (
          <section className="um-block um-danger">
            <div>
              <h4 className="um-block-title">Delete data source</h4>
              <div className="um-hint">Only possible while no assistant and no user uses it.</div>
            </div>
            <button type="button" className="um-btn um-btn-danger" onClick={() => setConfirm('delete')}><FontAwesomeIcon icon={faTrash} /> Delete</button>
          </section>
        )}
      </div>

      {confirm === 'delete' && (
        <ConfirmDialog title={`Delete ${original.name}?`} text="The stored credentials are deleted with it." confirmLabel="Delete"
          danger onConfirm={remove} onCancel={() => setConfirm(null)} />
      )}
      {confirm === 'duplicate' && (
        <DuplicateDialog source={original} onCancel={() => setConfirm(null)}
          onDone={(copy) => { setConfirm(null); flash('success', `Copied as ${copy.name}.`); onSaved(copy); }} />
      )}
      {confirm === 'reset' && (
        <ConfirmDialog title="Reset the pinned host key?" text="Only do this if the jump host was rebuilt. The next successful test pins whatever key it presents."
          confirmLabel="Reset" danger onConfirm={resetHostKey} onCancel={() => setConfirm(null)} />
      )}
    </>
  );
}

export default function DataSourcesSection({ flash, overview }) {
  const { client, meta } = useConsole();
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);       // { source, isNew, version }

  const load = useCallback(async () => {
    setError('');
    try { setList(await client.get('/api/admin/data-sources')); } catch (e) { setError(e.message); }
  }, [client]);
  useEffect(() => { load(); }, [load]);

  // After a save or test: reload the full record (with its usage) and remount
  // the editor on it, so the saved state is the new baseline.
  // `result`: a test result to keep showing after the remount.
  const onSaved = (saved, result = null) => {
    client.get('/api/admin/data-sources').then((all) => {
      setList(all);
      setEditing({ source: all.find((s) => s.id === saved.id) || saved, isNew: false, version: Date.now(), result });
    }).catch(() => setEditing({ source: saved, isNew: false, version: Date.now(), result }));
  };

  if (editing) {
    return (
      <SourceEditor key={editing.isNew ? 'new' : `${editing.source.id}-${editing.version || 0}`}
        original={editing.source} isNew={editing.isNew} flash={flash} initialResult={editing.result}
        onClose={() => { setEditing(null); load(); }} onSaved={onSaved}
        onDeleted={() => { setEditing(null); load(); }} />
    );
  }

  return (
    <>
      <div className="um-section-bar">
        <div className="um-subtitle">Whose data, in which data model — and how this instance reaches it</div>
        <div className="um-topbar-right">
          <button type="button" className="um-icon-btn" onClick={load} title="Reload"><FontAwesomeIcon icon={faArrowsRotate} /></button>
          <button type="button" className="um-btn um-btn-primary" onClick={() => setEditing({ source: { ...EMPTY_SOURCE }, isNew: true })}
            disabled={overview && !overview.secrets_configured}>
            <FontAwesomeIcon icon={faPlus} /> New data set
          </button>
        </div>
      </div>
      {overview && !overview.secrets_configured && (
        <div className="um-note warn"><FontAwesomeIcon icon={faTriangleExclamation} /> SECRETS_KEY is not configured on this instance, so credentials cannot be stored. Add it as a deployment secret (see docs/admin-console/README.md).</div>
      )}
      {error && <div className="um-hint error">{error}</div>}
      {!list ? <div className="um-loading"><Spinner /></div> : list.length === 0 ? (
        <div className="um-empty-card">
          <FontAwesomeIcon icon={faCircleInfo} />
          <div>No data sources yet. Until one is bound to an assistant, the data agents use the environment variables of this deployment (Assistants page).</div>
        </div>
      ) : (
        <div className="um-card-grid">
          {list.map((s) => (
            <button key={s.id} type="button" className="um-ds-card" onClick={() => setEditing({ source: s, isNew: false })}>
              <div className="um-ds-card-head">
                <StatusIcon status={s.last_test_ok === true ? 'ok' : s.last_test_ok === false ? 'fail' : null} />
                <strong>{s.name}</strong>
                <code className="um-ds-key">{s.key}</code>
              </div>
              <div className="um-ds-meta">
                <span className="um-chip strong">{(s.model || '?').toUpperCase()}</span>
                <span className="um-chip">{s.variant ? (meta.variants?.find((v) => v.key === s.variant)?.label || s.variant) : 'variant not set'}</span>
              </div>
              <div className="um-ds-target">{s.target}</div>
              <div className="um-ds-meta">
                {s.ssh_enabled && <span className="um-chip"><FontAwesomeIcon icon={faKey} /> SSH{s.ssh_host_key ? ' · pinned' : ' · not pinned'}</span>}
                {Object.keys(s.schema_map || {}).length > 0 && (
                  <span className="um-chip"><FontAwesomeIcon icon={faLayerGroup} /> {Object.entries(s.schema_map).map(([a, b]) => `${a}→${b}`).join(', ')}</span>
                )}
                {!s.secrets_readable && (s.has_db_password || s.has_ssh_key) && <span className="um-chip warn">secrets unreadable</span>}
                <span className="um-muted">tested {s.last_test_at ? relativeTime(s.last_test_at) : 'never'}</span>
              </div>
              <div className="um-ds-usage">
                {(s.usage?.assistants || []).map((a) => <span key={a} className="um-chip">{a}</span>)}
                {s.usage?.users?.length > 0 && <span className="um-chip">{s.usage.users.length} user{s.usage.users.length === 1 ? '' : 's'}</span>}
                {!s.usage?.assistants?.length && !s.usage?.users?.length && <span className="um-muted">not used yet</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
